import { getStorage, ref, getBlob } from "firebase/storage";
import { useMemo } from "react";
import Card from "react-bootstrap/Card";
import { useNavigate, useParams } from "react-router-dom";
import { useAuthState } from "react-firebase-hooks/auth";
import { useDownloadURL } from "react-firebase-hooks/storage";
import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";
import { getAuth } from "firebase/auth";
import PdfViewer from "../../components/documents/pdfViewer";
import useMetadata from "../../hooks/useMetadata";
import LoadingAlert from "../../components/common/loadingAlert";

const auth = getAuth();
const storage = getStorage();

function downloadFile(blob: Blob, name: string) {
	const link = document.createElement("a");

	link.href = URL.createObjectURL(blob);
	link.download = name;

	document.body.appendChild(link);

	link.click();
	link.remove();

	setTimeout(() => URL.revokeObjectURL(link.href), 5000);
}

export default function DocumentsView() {
	const navigate = useNavigate();

	const [user, userLoading] = useAuthState(auth);

	if (!user && !userLoading) {
		navigate("/account");
	}

	const { projectId, "*": path } = useParams();
	if (!projectId) throw "DocumentsView page requires a projectId parameter.";
	if (!path) throw "DocumentsView page requires a path.";

	const documentRef = useMemo(
		() => ref(storage, `Documents/${projectId}/${path}`),
		[projectId, path],
	);

	const [documentUrl, , documentUrlError] = useDownloadURL(documentRef);
	const [documentMetadata, , documentMetadataError] = useMetadata(documentRef);

	if (!documentUrl || !documentMetadata) {
		return <LoadingAlert error={documentUrlError || documentMetadataError} />;
	}

	return (
		<div>
			<div className="mb-2">
				<b>Dokument</b>: {documentMetadata.name}
			</div>

			<Row>
				<Col xs="6">
					<Card role="button" className="mb-2 clickable-card" onClick={() => navigate(-1)}>
						<Card.Body>
							<Row>
								<Col xs="auto">
									<i className="bi bi-arrow-left-circle fs-1" />
								</Col>
								<Col className="d-flex align-items-center">Zurück</Col>
							</Row>
						</Card.Body>
					</Card>
				</Col>
				<Col xs="6">
					<Card
						role="button"
						className="mb-2 clickable-card"
						onClick={async () => {
							const blob = await getBlob(documentRef);

							downloadFile(blob, documentMetadata.name);
						}}>
						<Card.Body>
							<Row>
								<Col xs="auto">
									<i className="bi bi-download fs-1" />
								</Col>
								<Col className="d-flex align-items-center">Herunterladen</Col>
							</Row>
						</Card.Body>
					</Card>
				</Col>
			</Row>

			{documentMetadata.contentType !== "application/pdf" ? (
				<object data={documentUrl} className="w-100 rounded object-viewer">
					<div className="text-muted">
						Der Dokumentenviewer kann diesen Dateityp nicht anzeigen. Lade die Datei stattdessen
						herunter.
					</div>
				</object>
			) : (
				<PdfViewer pdfUrl={documentUrl} />
			)}
		</div>
	);
}
